import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowDownLeft, ArrowUpRight, User, MoreVertical, X } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useToast } from '../ui/Toast';
import { api, Wallet, Client } from '../../lib/api';
import { isCryptoCurrency, formatRateForForm } from '../../lib/rates.js';
import { useReferenceRate } from '../../hooks/useReferenceRate.js';
import { ModeTabsToggle } from './ModeTabsToggle';
import { AmountInputField } from './AmountInputField';
import { RateCardsSection } from './RateCardsSection';
import { OperationSuccessModal } from './OperationSuccessModal';
import { ExpenseForm } from './ExpenseForm';
import { TransferForm } from './TransferForm';
import { OwnerOperationForm } from './OwnerOperationForm';
import { CryptoOperationForm } from './CryptoOperationForm';
import { ClientSearchAutocomplete } from './ClientSearchAutocomplete';
import { TxHashField } from './TxHashField';
import { isCashierEntityAllowed } from './constants';
import type { OperationMode, WalletOption, SuccessData } from './types';

interface CreateOperationFormProps {
  onSubmit: () => void;
}

// Zod schema for form validation - aligned with API
const operationFormSchema = z.object({
  mode: z.enum(['buy', 'sell']).optional(),
  clientGivesAmount: z.string()
    .refine(val => val === '' || val === '0' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), 'Must be a valid number'),
  clientGetsAmount: z.string()
    .refine(val => val === '' || val === '0' || (!isNaN(parseFloat(val)) && parseFloat(val) >= 0), 'Must be a valid number'),
  clientGivesCurrency: z.string().min(1, 'Please select a currency'),
  walletInId: z.string().min(1, 'Please select a wallet'),
  walletOutId: z.string().min(1, 'Please select a wallet'),
  rate: z.string()
    .min(1, 'Rate is required')
    .refine(val => !isNaN(parseFloat(val)) && parseFloat(val) > 0, 'Must be a positive number'),
  clientId: z.string().min(1, 'Client is required'),
  comment: z.string().optional(),
  isPendingDeal: z.boolean().optional(),
  debtAmountIn: z.string().optional(),
  debtAmountOut: z.string().optional(),
  expectedPaymentDateIn: z.string().optional(),
  expectedPaymentDateOut: z.string().optional(),
  transactionDate: z.string().optional(),
  cryptoTxHash: z.string().optional(),
}).superRefine((data, ctx) => {
  const isSell = data.mode === 'sell';
  const gives = parseFloat(data.clientGivesAmount) || 0;
  const gets = parseFloat(data.clientGetsAmount) || 0;
  const debtIn = parseFloat(data.debtAmountIn || '0') || 0;
  const debtOut = parseFloat(data.debtAmountOut || '0') || 0;

  // BUY: totalIn = gives + debtIn, totalOut = gets + debtOut
  // SELL: totalIn = gets + debtIn,  totalOut = gives + debtOut
  const totalIn = (isSell ? gets : gives) + debtIn;
  const totalOut = (isSell ? gives : gets) + debtOut;

  // totalIn error → in SELL shows on clientGetsAmount (top field), in BUY on clientGivesAmount (top field)
  const totalInField = isSell ? 'clientGetsAmount' : 'clientGivesAmount';
  // totalOut error → in SELL shows on clientGivesAmount (bottom field), in BUY on clientGetsAmount (bottom field)
  const totalOutField = isSell ? 'clientGivesAmount' : 'clientGetsAmount';

  if (data.isPendingDeal) {
    if (totalIn <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Total amount must be greater than 0', path: [totalInField] });
    }
    if (totalOut <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Total amount must be greater than 0', path: [totalOutField] });
    }
  } else {
    if (gives <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount must be greater than 0', path: ['clientGivesAmount'] });
    }
    if (gets <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Amount must be greater than 0', path: ['clientGetsAmount'] });
    }
  }
});

interface OperationFormData {
  mode?: 'buy' | 'sell';
  clientGivesAmount: string;
  clientGetsAmount: string;
  clientGivesCurrency: string;
  walletInId: string;
  walletOutId: string;
  rate: string;
  clientId: string;
  comment?: string;
  isPendingDeal?: boolean;
  debtAmountIn?: string;
  debtAmountOut?: string;
  expectedPaymentDateIn?: string;
  expectedPaymentDateOut?: string;
  transactionDate?: string;
  cryptoTxHash?: string;
}

// Currency icons mapping
const CURRENCY_ICONS: Record<string, string> = {
  'RUB': '₽',
  'EUR': '€',
  'USD': '$',
  'GBP': '£',
  'USDT': '₮',
  'BTC': '₿',
  'ETH': 'Ξ',
  'USDC': '$',
};

export function CreateOperationForm({ onSubmit }: CreateOperationFormProps) {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const toast = useToast();
  const [mode, setMode] = useState<OperationMode>('buy');
  const [spread, setSpread] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rateEdited, setRateEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  // Wallet state
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [cryptoReserve, setCryptoReserve] = useState(0);

  // Selected client state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientMenu, setShowClientMenu] = useState(false);
  const clientMenuRef = useRef<HTMLDivElement>(null);

  // Ref for cash amount input
  const cashAmountInputRef = useRef<HTMLInputElement | null>(null);

  // Ref to skip conversion during reset
  const isResettingRef = useRef(false);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<OperationFormData>({
    resolver: zodResolver(operationFormSchema),
    defaultValues: {
      mode: 'buy',
      clientGivesAmount: '',
      clientGetsAmount: '',
      clientGivesCurrency: 'EUR',
      walletInId: '',
      walletOutId: '',
      rate: '1.085',
      clientId: '',
      comment: '',
      isPendingDeal: false,
      debtAmountIn: '',
      debtAmountOut: '',
      expectedPaymentDateIn: '',
      expectedPaymentDateOut: '',
      transactionDate: new Date().toISOString().split('T')[0],
      cryptoTxHash: '',
    },
  });

  const clientGivesAmount = watch('clientGivesAmount');
  const clientGetsAmount = watch('clientGetsAmount');
  const clientGivesCurrency = watch('clientGivesCurrency');
  const rate = watch('rate');
  const walletInId = watch('walletInId');
  const walletOutId = watch('walletOutId');
  // These must be at top level so RHF subscribes reactively
  const isPendingDeal = watch('isPendingDeal');
  const debtAmountIn = watch('debtAmountIn');
  const debtAmountOut = watch('debtAmountOut');

  // Derive cash currency from selected cash wallet
  useEffect(() => {
    if (!walletInId || wallets.length === 0) return;
    const cashWallet = wallets.find(w => !isCryptoCurrency(w.currencyCode) && w.id === walletInId);
    if (cashWallet?.currencyCode) {
      setValue('clientGivesCurrency', cashWallet.currencyCode);
    }
  }, [walletInId, wallets, setValue]);

  // Focus cash amount input on initial load
  useEffect(() => {
    cashAmountInputRef.current?.focus();
  }, []);

  // Allowed currencies for cashier
  const ALLOWED_CURRENCIES = ['EUR', 'USD', 'RUB', 'USDT', 'BTC', 'ETH', 'USDC', 'BNB', 'SOL', 'XRP'];

  // Fetch wallets on mount
  useEffect(() => {
    async function fetchWallets() {
      if (!currentEntity?.id) return;

      // Only fetch wallets for FNK entity
      if (!isCashierEntityAllowed(currentEntity.code)) {
        setWallets([]);
        setWalletsLoading(false);
        return;
      }

      try {
        setWalletsLoading(true);
        const result = await api.admin.wallets.list(currentEntity.id);
        // Filter to only active wallets with allowed currencies
        const filtered = result.data.filter(w =>
          w.isActive && ALLOWED_CURRENCIES.includes(w.currencyCode.toUpperCase())
        );
        setWallets(filtered);
      } catch (err) {
        console.error('Failed to load wallets:', err);
        toast.error('Failed to load wallets');
      } finally {
        setWalletsLoading(false);
      }
    }
    fetchWallets();
  }, [currentEntity?.id, currentEntity?.code, toast]);

  // Close client menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientMenuRef.current && !clientMenuRef.current.contains(event.target as Node)) {
        setShowClientMenu(false);
      }
    };
    if (showClientMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClientMenu]);

  // Client menu handler
  const handleClearClient = () => {
    setSelectedClient(null);
    setValue('clientId', '');
    setShowClientMenu(false);
  };

  // Separate wallets into fiat and crypto
  const fiatWallets: WalletOption[] = useMemo(() => wallets
    .filter(w => !isCryptoCurrency(w.currencyCode))
    .map(w => ({
      id: w.id,
      name: w.name,
      currency: w.currencyCode,
      icon: CURRENCY_ICONS[w.currencyCode.toUpperCase()] || '€',
      balance: 0,
    })), [wallets]);

  const cryptoWallets: WalletOption[] = useMemo(() => {
    const list: WalletOption[] = wallets
      .filter(w => isCryptoCurrency(w.currencyCode))
      .map(w => ({
        id: w.id,
        name: w.name,
        currency: w.currencyCode,
        icon: CURRENCY_ICONS[w.currencyCode.toUpperCase()] || '₮',
        balance: 0,
      }));

    const cashWallets: WalletOption[] = wallets
      .filter(w => w.type === 'cash')
      .map(w => ({
        id: w.id,
        name: w.name,
        currency: w.currencyCode,
        icon: CURRENCY_ICONS[w.currencyCode.toUpperCase()] || '€',
        balance: 0,
      }));

    if (cashWallets.length > 0) {
      list.push({
        id: 'sep-cash',
        name: '──────────',
        currency: '',
        icon: '',
        isSeparator: true,
      });
      list.push(...cashWallets);
    }
    return list;
  }, [wallets]);

  // Get selected crypto currency
  const getClientGetsCurrency = useCallback(() => {
    const wallet = cryptoWallets.find(w => w.id === walletOutId);
    return wallet?.currency || 'USDT';
  }, [walletOutId, cryptoWallets]);

  const { referenceRate, fetchReferenceRate, isLoadingRate, rateSource } = useReferenceRate({
    clientGivesCurrency,
    getCryptoCurrency: getClientGetsCurrency,
    getFiatRatesUsd: () => api.rates.fiatUsd(currentEntity!.id),
    setRateValue: (v) => {
      // Only set default rate from API when amounts are not yet entered;
      // once amounts are entered, rate is auto-computed from them
      const gives = parseFloat(clientGivesAmount) || 0;
      const gets  = parseFloat(clientGetsAmount)  || 0;
      if (gives <= 0 && gets <= 0) setValue('rate', v);
    },
    onError: (msg) => toast.error(msg),
  });

  // Update crypto reserve when wallet changes
  useEffect(() => {
    const wallet = cryptoWallets.find(w => w.id === walletOutId);
    if (wallet) {
      setCryptoReserve(wallet.balance || 0);
    }
  }, [walletOutId, cryptoWallets]);

  // Fetch rate only when fiat or crypto selection changes (fetchReferenceRate is stable)
  useEffect(() => {
    if (clientGivesCurrency) {
      fetchReferenceRate();
    }
  }, [clientGivesCurrency, walletOutId, fetchReferenceRate]);


  // Auto-compute actual rate and spread from entered amounts (including debt totals)
  useEffect(() => {
    // Variable naming: givesNum = ALWAYS fiat (EUR), getsNum = ALWAYS crypto (USDT)
    // These names map to form fields, NOT to client perspective per mode.
    const givesNum = parseFloat(clientGivesAmount) || 0;  // fiat
    const getsNum  = parseFloat(clientGetsAmount)  || 0;  // crypto

    // Include debt amounts in totals when pending deal is active.
    // debtAmountIn/Out correspond to TOP/BOTTOM form positions:
    //   BUY:  TOP=fiat, BOTTOM=crypto  → debtIn adds to fiat, debtOut adds to crypto
    //   SELL: TOP=crypto, BOTTOM=fiat  → debtIn adds to crypto, debtOut adds to fiat
    const debtInNum  = isPendingDeal ? (parseFloat(debtAmountIn  || '0') || 0) : 0;
    const debtOutNum = isPendingDeal ? (parseFloat(debtAmountOut || '0') || 0) : 0;

    const totalFiat   = givesNum + (mode === 'buy' ? debtInNum  : debtOutNum);
    const totalCrypto = getsNum  + (mode === 'buy' ? debtOutNum : debtInNum);

    if (totalFiat <= 0 || totalCrypto <= 0) {
      setSpread(0);
      return;
    }

    // rate = fiat-per-crypto (EUR per USDT), same direction as referenceRate
    const computedRate = totalFiat / totalCrypto;
    setValue('rate', computedRate.toFixed(4));

    if (referenceRate > 0) {
      // BUY:  higher rate = client pays more EUR per USDT = good → positive spread
      // SELL: lower rate = we pay less EUR per USDT = good → invert so positive = good
      const spreadPercent = mode === 'sell'
        ? ((referenceRate - computedRate) / referenceRate) * 100
        : ((computedRate - referenceRate) / referenceRate) * 100;
      setSpread(spreadPercent);
    }
  }, [clientGivesAmount, clientGetsAmount, isPendingDeal, debtAmountIn, debtAmountOut, mode, referenceRate, setValue]);

  // Filter numeric input
  const filterNumericInput = (value: string): string => {
    let filtered = value.replace(/[^0-9.]/g, '');
    const parts = filtered.split('.');
    if (parts.length > 2) {
      filtered = parts[0] + '.' + parts.slice(1).join('');
    }
    return filtered;
  };

  const handleClientGivesAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = filterNumericInput(e.target.value);
    setValue('clientGivesAmount', filtered);
    clearErrors('clientGivesAmount');
  };

  const handleClientGetsAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = filterNumericInput(e.target.value);
    setValue('clientGetsAmount', filtered);
    clearErrors('clientGetsAmount');
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = filterNumericInput(e.target.value);
    setValue('rate', filtered);
    setRateEdited(true);
    clearErrors('rate');
  };

  const handleWalletInChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('walletInId', e.target.value);
    clearErrors('walletInId');
  };

  const handleWalletOutChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('walletOutId', e.target.value);
    clearErrors('walletOutId');
  };

  const handleModeChange = (newMode: OperationMode) => {
    if (mode !== newMode) {
      setMode(newMode);
      if (newMode === 'buy' || newMode === 'sell') {
        setValue('mode', newMode);
      }
      setValue('clientGivesAmount', '');
      setValue('clientGetsAmount', '');
    }
  };

  const onFormSubmit = async (data: OperationFormData) => {
    if (!currentEntity?.id) {
      toast.error('No entity selected');
      return;
    }

    setIsSubmitting(true);

    try {
      const clientGetsCurrency = getClientGetsCurrency();
      const status = data.isPendingDeal ? 'open' : 'closed';

      // Removed unused amount variables

      // DB stores rate as "1 currency_in → X currency_out".
      // BUY:  currency_in=EUR, currency_out=USDT → stored as USDT per EUR
      // SELL: currency_in=USDT, currency_out=EUR → stored as EUR per USDT
      // Form `rate` and `referenceRate` are both in EUR/USDT direction (fiat-per-crypto).
      // For BUY we invert (1/rate) to get USDT/EUR. For SELL we use as-is.
      const rateNum = parseFloat(data.rate || '1') || 1;
      const submittedExchangeRate = mode === 'buy'
        ? (1 / rateNum).toFixed(4)               // USDT/EUR (e.g. 1.2000)
        : data.rate;                              // EUR/USDT for SELL (unchanged)
      const submittedOracleRate = mode === 'buy'
        ? (1 / referenceRate).toFixed(8)          // USDT/EUR (e.g. 1.1500)
        : referenceRate.toString();               // EUR/USDT for SELL (unchanged)

      const basePayload = {
        type: 'exchange' as const,
        status: status as 'open' | 'closed',
        exchangeRate: submittedExchangeRate,
        oracleRate: submittedOracleRate,
        clientId: data.clientId,
        ...(data.comment && { comment: data.comment }),
        ...(data.transactionDate && !data.isPendingDeal && { transactionDate: data.transactionDate }),
      };

      const rawGivesNum = parseFloat(data.clientGivesAmount || '0');
      const rawGetsNum = parseFloat(data.clientGetsAmount || '0');
      const debtInNum  = parseFloat(data.debtAmountIn  || '0') || 0;
      const debtOutNum = parseFloat(data.debtAmountOut || '0') || 0;

      let topActual = 0;
      let bottomActual = 0;

      if (mode === 'buy') {
        topActual    = rawGivesNum + debtInNum;   // Total fiat in = immediate + debt in
        bottomActual = rawGetsNum  + debtOutNum;  // Total crypto out = immediate + debt out
      } else {
        topActual    = rawGetsNum  + debtInNum;   // Total crypto in = immediate + debt in
        bottomActual = rawGivesNum + debtOutNum;  // Total fiat out = immediate + debt out
      }

      const getDecimals = (currency: string) => {
        if (currency === 'USDT' || currency === 'USDC') return 0;
        if (['BTC', 'ETH', 'BNB', 'SOL', 'XRP'].includes(currency.toUpperCase())) return 6;
        return 0;
      };

      const transactionPayload = mode === 'buy'
        ? {
          ...basePayload,
          amountIn: topActual.toFixed(getDecimals(data.clientGivesCurrency)),
          currencyIn: data.clientGivesCurrency,
          walletInId: data.walletInId,
          amountOut: bottomActual.toFixed(getDecimals(clientGetsCurrency)),
          currencyOut: clientGetsCurrency,
          walletOutId: data.walletOutId,
          ...(data.cryptoTxHash && { txHashOut: data.cryptoTxHash }),
          ...(status === 'open' && {
            expectedAmount: data.clientGetsAmount,
            debtAmount: data.debtAmountOut || data.debtAmountIn || undefined,
            debtAmountIn: data.debtAmountIn || undefined,
            debtAmountOut: data.debtAmountOut || undefined,
            expectedPaymentDateIn: data.expectedPaymentDateIn || undefined,
            expectedPaymentDateOut: data.expectedPaymentDateOut || undefined,
          }),
        }
        : {
          ...basePayload,
          amountIn: topActual.toFixed(getDecimals(clientGetsCurrency)),
          currencyIn: clientGetsCurrency,
          walletInId: data.walletOutId,
          amountOut: bottomActual.toFixed(getDecimals(data.clientGivesCurrency)),
          currencyOut: data.clientGivesCurrency,
          walletOutId: data.walletInId,
          ...(data.cryptoTxHash && { txHashIn: data.cryptoTxHash }),
          ...(status === 'open' && {
            expectedAmount: data.clientGivesAmount,
            debtAmount: data.debtAmountOut || data.debtAmountIn || undefined,
            debtAmountIn: data.debtAmountIn || undefined,
            debtAmountOut: data.debtAmountOut || undefined,
            expectedPaymentDateIn: data.expectedPaymentDateIn || undefined,
            expectedPaymentDateOut: data.expectedPaymentDateOut || undefined,
          }),
        };

      await api.transactions.create(currentEntity.id, transactionPayload);

      setSuccessData({
        mode: mode.toUpperCase(),
        clientGivesAmount: data.clientGivesAmount,
        clientGivesCurrency: data.clientGivesCurrency,
        clientGetsAmount: data.clientGetsAmount,
        clientGetsCurrency,
        rate: data.rate,
        clientId: data.clientId,
        transactionDate: data.transactionDate,
      });

      setShowSuccessModal(true);

      toast.success('Transaction created successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setSuccessData(null);
    setSelectedClient(null);
    setRateEdited(false);

    // Prevent conversion effect from recalculating during reset
    isResettingRef.current = true;

    const newRate = formatRateForForm(referenceRate);
    reset({
      mode: mode as 'buy' | 'sell',
      clientGivesAmount: '',
      clientGetsAmount: '',
      clientGivesCurrency: successData?.clientGivesCurrency || 'EUR',
      walletInId: '',
      walletOutId: '',
      rate: newRate,
      clientId: '',
      comment: '',
      isPendingDeal: false,
      debtAmountIn: '',
      debtAmountOut: '',
      expectedPaymentDateIn: '',
      expectedPaymentDateOut: '',
      transactionDate: new Date().toISOString().split('T')[0],
      cryptoTxHash: '',
    });

    // Re-enable conversion after reset completes
    setTimeout(() => {
      isResettingRef.current = false;
    }, 0);

    onSubmit();
  };

  const getError = (field: keyof OperationFormData) => errors[field]?.message;

  return (
    <>
      {/* Expense mode — standalone form, no client panel */}
      {mode === 'expense' && (
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.expense', 'Expense')}
            </h2>
          </div>
          <ModeTabsToggle
            mode={mode}
            onModeChange={handleModeChange}
            buyLabel={t('cashier.createOperation.buy', 'BUY')}
            sellLabel={t('cashier.createOperation.sell', 'SELL')}
            expenseLabel={t('cashier.createOperation.expense', 'EXPENSE')}
            transferLabel={t('cashier.createOperation.transfer', 'TRANSFER')}
            ownerLabel={t('cashier.createOperation.owner', 'OWNER')}
            cryptoLabel={t('cashier.createOperation.crypto', 'CRYPTO')}
          />
          <ExpenseForm onSubmit={onSubmit} />
        </div>
      )}

      {/* Transfer mode — standalone form, no client panel */}
      {mode === 'transfer' && (
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.transferMode', 'Transfer')}
            </h2>
          </div>
          <ModeTabsToggle
            mode={mode}
            onModeChange={handleModeChange}
            buyLabel={t('cashier.createOperation.buy', 'BUY')}
            sellLabel={t('cashier.createOperation.sell', 'SELL')}
            expenseLabel={t('cashier.createOperation.expense', 'EXPENSE')}
            transferLabel={t('cashier.createOperation.transfer', 'TRANSFER')}
            ownerLabel={t('cashier.createOperation.owner', 'OWNER')}
            cryptoLabel={t('cashier.createOperation.crypto', 'CRYPTO')}
          />
          <TransferForm onSubmit={onSubmit} />
        </div>
      )}

      {/* Owner mode — standalone form for deposit/withdrawal */}
      {mode === 'owner' && (
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.ownerMode', 'Owner Operations')}
            </h2>
          </div>
          <ModeTabsToggle
            mode={mode}
            onModeChange={handleModeChange}
            buyLabel={t('cashier.createOperation.buy', 'BUY')}
            sellLabel={t('cashier.createOperation.sell', 'SELL')}
            expenseLabel={t('cashier.createOperation.expense', 'EXPENSE')}
            transferLabel={t('cashier.createOperation.transfer', 'TRANSFER')}
            ownerLabel={t('cashier.createOperation.owner', 'OWNER')}
            cryptoLabel={t('cashier.createOperation.crypto', 'CRYPTO')}
          />
          <OwnerOperationForm onSubmit={onSubmit} />
        </div>
      )}

      {/* Crypto mode — stablecoin ↔ stablecoin exchange (USDT/USDC), no unified-rate block */}
      {mode === 'crypto' && (
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.cryptoMode', 'Crypto Exchange')}
            </h2>
          </div>
          <ModeTabsToggle
            mode={mode}
            onModeChange={handleModeChange}
            buyLabel={t('cashier.createOperation.buy', 'BUY')}
            sellLabel={t('cashier.createOperation.sell', 'SELL')}
            expenseLabel={t('cashier.createOperation.expense', 'EXPENSE')}
            transferLabel={t('cashier.createOperation.transfer', 'TRANSFER')}
            ownerLabel={t('cashier.createOperation.owner', 'OWNER')}
            cryptoLabel={t('cashier.createOperation.crypto', 'CRYPTO')}
          />
          <div className="p-4">
            <CryptoOperationForm onSubmit={onSubmit} />
          </div>
        </div>
      )}

      <form
        className={`grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-6 ${mode === 'expense' || mode === 'transfer' || mode === 'owner' || mode === 'crypto' ? 'hidden' : ''}`}
        onSubmit={handleSubmit(onFormSubmit)}
      >
        {/* Left Panel - Exchange */}
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
          {/* Panel Header */}
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.exchange', 'Exchange')}
            </h2>
          </div>

          {/* Mode Tabs */}
          <ModeTabsToggle
            mode={mode}
            onModeChange={handleModeChange}
            buyLabel={t('cashier.createOperation.buy', 'BUY')}
            sellLabel={t('cashier.createOperation.sell', 'SELL')}
            expenseLabel={t('cashier.createOperation.expense', 'EXPENSE')}
            transferLabel={t('cashier.createOperation.transfer', 'TRANSFER')}
            ownerLabel={t('cashier.createOperation.owner', 'OWNER')}
            cryptoLabel={t('cashier.createOperation.crypto', 'CRYPTO')}
          />

          {/* Exchange Content */}
          <div className="p-6">
            {/* Client Gives - swaps based on mode */}
            <AmountInputField
              sectionLabel={t('cashier.createOperation.amountToReceiveSection', 'Сумма к получению')}
              floatingLabel={mode === 'buy' ? 'AMOUNT' : 'AMOUNT'}
              balanceLabel={mode === 'buy' ? t('cashier.createOperation.balance', 'Balance') : t('cashier.createOperation.reserve', 'Reserve')}
              balanceValue={mode === 'buy' ? '∞' : `${cryptoReserve.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: getClientGetsCurrency() === 'USDT' || getClientGetsCurrency() === 'USDC' ? 0 : 6 })} ${getClientGetsCurrency()}`}
              placeholder={mode === 'buy' ? '0' : (getClientGetsCurrency() === 'USDT' || getClientGetsCurrency() === 'USDC' ? '0' : '0.000000')}
              value={mode === 'buy' ? clientGivesAmount : clientGetsAmount}
              onChange={(e) => {
                if (mode === 'buy') handleClientGivesAmountChange(e);
                else handleClientGetsAmountChange(e);
              }}
              walletValue={mode === 'buy' ? walletInId : walletOutId}
              onWalletChange={mode === 'buy' ? handleWalletInChange : handleWalletOutChange}
              wallets={mode === 'buy' ? fiatWallets : cryptoWallets}
              walletsLoading={walletsLoading}
              error={mode === 'buy' ? getError('clientGivesAmount') : getError('clientGetsAmount')}
              walletError={mode === 'buy' ? getError('walletInId') : getError('walletOutId')}
              inputName={mode === 'buy' ? 'clientGivesAmount' : 'clientGetsAmount'}
              walletName={mode === 'buy' ? 'walletInId' : 'walletOutId'}
              ref={mode === 'buy' ? cashAmountInputRef : undefined}
            />

            {/* Total IN = immediate + debt_in (shown when pending deal active) */}
            {isPendingDeal && (() => {
              const immediateRaw = parseFloat(mode === 'buy' ? clientGivesAmount : clientGetsAmount) || 0;
              const debtIn = parseFloat(debtAmountIn || '0') || 0;
              const total = immediateRaw + debtIn;
              const currency = mode === 'buy' ? clientGivesCurrency : getClientGetsCurrency();
              const isBigCrypto = ['BTC', 'ETH', 'BNB', 'SOL', 'XRP'].includes(currency.toUpperCase());
              const decimals = isBigCrypto ? 6 : 0;
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 14px', marginTop: '-4px', marginBottom: '4px',
                  borderRadius: '8px', background: 'color-mix(in srgb, var(--ctp-teal) 8%, var(--color-surface))',
                  border: '1px dashed color-mix(in srgb, var(--ctp-teal) 40%, transparent)',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {'–'}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', color: 'var(--ctp-teal, #179299)' }}>
                    {total > 0 ? total.toFixed(decimals) : '—'} {currency}
                  </span>
                </div>
              );
            })()}

            {/* Arrow divider between the two field groups */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '8px 0',
              gap: '10px',
            }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
              <div style={{
                width: '32px', height: '32px',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--color-surface-hover)',
                border: '1px solid var(--color-border)',
                color: 'var(--ctp-teal, #179299)',
                fontSize: '16px', fontWeight: 700,
                flexShrink: 0,
              }}>
                ↓
              </div>
              <div style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
            </div>

            {/* Client Receives - swaps based on mode */}
            <AmountInputField
              sectionLabel={t('cashier.createOperation.amountToGiveSection', 'Сумма к выдаче')}
              floatingLabel="AMOUNT"
              balanceLabel={mode === 'buy' ? t('cashier.createOperation.reserve', 'Reserve') : t('cashier.createOperation.balance', 'Balance')}
              balanceValue={mode === 'buy' ? `${cryptoReserve.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: getClientGetsCurrency() === 'USDT' || getClientGetsCurrency() === 'USDC' ? 0 : 6 })} ${getClientGetsCurrency()}` : '∞'}
              placeholder={mode === 'buy' ? (getClientGetsCurrency() === 'USDT' || getClientGetsCurrency() === 'USDC' ? '0' : '0.000000') : '0'}
              value={mode === 'buy' ? clientGetsAmount : clientGivesAmount}
              onChange={(e) => {
                if (mode === 'buy') handleClientGetsAmountChange(e);
                else handleClientGivesAmountChange(e);
              }}
              walletValue={mode === 'buy' ? walletOutId : walletInId}
              onWalletChange={mode === 'buy' ? handleWalletOutChange : handleWalletInChange}
              wallets={mode === 'buy' ? cryptoWallets : fiatWallets}
              walletsLoading={walletsLoading}
              error={mode === 'buy' ? getError('clientGetsAmount') : getError('clientGivesAmount')}
              walletError={mode === 'buy' ? getError('walletOutId') : getError('walletInId')}
              inputName={mode === 'buy' ? 'clientGetsAmount' : 'clientGivesAmount'}
              walletName={mode === 'buy' ? 'walletOutId' : 'walletInId'}
              _isSecondary
            />

            {/* Total OUT = immediate + debt_out (shown when pending deal active) */}
            {isPendingDeal && (() => {
              const immediateRaw = parseFloat(mode === 'buy' ? clientGetsAmount : clientGivesAmount) || 0;
              const debtOut = parseFloat(debtAmountOut || '0') || 0;
              const total = immediateRaw + debtOut;
              const currency = mode === 'buy' ? getClientGetsCurrency() : clientGivesCurrency;
              const isBigCrypto = ['BTC', 'ETH', 'BNB', 'SOL', 'XRP'].includes(currency.toUpperCase());
              const decimals = isBigCrypto ? 6 : 0;
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 14px', marginTop: '-4px', marginBottom: '4px',
                  borderRadius: '8px', background: 'color-mix(in srgb, var(--ctp-teal) 8%, var(--color-surface))',
                  border: '1px dashed color-mix(in srgb, var(--ctp-teal) 40%, transparent)',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {'–'}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', color: 'var(--ctp-teal, #179299)' }}>
                    {total > 0 ? total.toFixed(decimals) : '—'} {currency}
                  </span>
                </div>
              );
            })()}

            {/* Rate Section */}
            {(() => {
              const cryptoCurrency = getClientGetsCurrency();
              const fiatCurrency = clientGivesCurrency;
              const isBigCrypto = ['BTC', 'ETH', 'BNB', 'SOL', 'XRP'].includes(cryptoCurrency.toUpperCase());
              const cryptoDecimals = isBigCrypto ? 6 : 0;

              // Oracle equivalent:
              // clientGivesAmount = ALWAYS fiat, clientGetsAmount = ALWAYS crypto
              // BUY:  debtIn → fiat (top), debtOut → crypto (bottom)
              // SELL: debtIn → crypto (top), debtOut → fiat (bottom)
              const fiatRaw   = parseFloat(clientGivesAmount) || 0;
              const cryptoRaw = parseFloat(clientGetsAmount)  || 0;
              const debtInVal  = parseFloat(debtAmountIn  || '0') || 0;
              const debtOutVal = parseFloat(debtAmountOut || '0') || 0;

              const fiatTotal   = isPendingDeal
                ? fiatRaw   + (mode === 'buy' ? debtInVal  : debtOutVal)
                : fiatRaw;
              const cryptoTotal = isPendingDeal
                ? cryptoRaw + (mode === 'buy' ? debtOutVal : debtInVal)
                : cryptoRaw;

              let oracleEquivalent: { label: string; cryptoTotal: string; fiatEquivalent: string; currency: string } | undefined;

              if (mode === 'buy' && fiatTotal > 0 && referenceRate > 0) {
                const oracleCrypto = fiatTotal / referenceRate;
                oracleEquivalent = {
                  label: 'По эталонному курсу, итого к выдаче',
                  cryptoTotal: `${fiatTotal.toFixed(2)} ${fiatCurrency}`,
                  fiatEquivalent: oracleCrypto.toFixed(cryptoDecimals),
                  currency: cryptoCurrency,
                };
              } else if (mode === 'sell' && cryptoTotal > 0 && referenceRate > 0) {
                const oracleFiat = cryptoTotal * referenceRate;
                oracleEquivalent = {
                  label: 'По эталонному курсу, итого к выдаче',
                  cryptoTotal: `${cryptoTotal.toFixed(cryptoDecimals)} ${cryptoCurrency}`,
                  fiatEquivalent: oracleFiat.toFixed(2),
                  currency: fiatCurrency,
                };
              }

              const fiatSymbol = fiatCurrency === 'EUR' ? '€' : fiatCurrency === 'USD' ? '$' : fiatCurrency === 'GBP' ? '£' : fiatCurrency;

              // Profit = how much better we do vs oracle (positive = good for us)
              // BUY:  we give crypto → profit = oracle_crypto - actual_crypto (give less = good)
              // SELL: we give fiat   → profit = oracle_fiat - actual_fiat (give less = good)
              let profit: { value: number; currency: string } | undefined;
              if (referenceRate > 0) {
                if (mode === 'buy' && fiatTotal > 0 && cryptoTotal > 0) {
                  profit = { value: (fiatTotal / referenceRate) - cryptoTotal, currency: cryptoCurrency };
                } else if (mode === 'sell' && cryptoTotal > 0 && fiatTotal > 0) {
                  profit = { value: (cryptoTotal * referenceRate) - fiatTotal, currency: fiatCurrency };
                }
              }

              return (
                <RateCardsSection
                  referenceRate={referenceRate}
                  actualRate={rate}
                  onActualRateChange={handleRateChange}
                  spread={spread}
                  rateError={getError('rate')}
                  isLoadingRate={isLoadingRate}
                  rateSource={rateSource}
                  cryptoSymbol={cryptoCurrency}
                  fiatSymbol={fiatSymbol}
                  oracleEquivalent={oracleEquivalent}
                  readOnly={true}
                  invertedDisplay={mode === 'buy'}
                  profit={profit}
                />
              );
            })()}

            {/* Date, Comment & Pending Deal */}
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              {/* Transaction Date */}
              {!isPendingDeal && (
                <div className="sm:w-44">
                  <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                    {t('cashier.createOperation.date', 'Date')}
                  </label>
                  <input
                    type="date"
                    className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-teal"
                    {...register('transactionDate')}
                  />
                  <p className="text-[11px] text-foreground-muted mt-1.5">
                    {t('cashier.createOperation.dateHint', 'Optional. Defaults to today.')}
                  </p>
                </div>
              )}

              {/* Comment */}
              <div className="flex-1">
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
                  {t('cashier.createOperation.comment', 'Comment')}
                </label>
                <input
                  type="text"
                  className="w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-teal placeholder:text-foreground-muted"
                  placeholder={t('cashier.createOperation.addNote', 'Add a note...')}
                  maxLength={200}
                  {...register('comment')}
                />
              </div>

              {/* Pending Deal Checkbox */}
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px] accent-ctp-teal cursor-pointer"
                    {...register('isPendingDeal')}
                  />
                  <span className="text-sm text-foreground whitespace-nowrap">
                    {t('cashier.createOperation.pendingDeal', 'Pending Deal')}
                  </span>
                </label>
              </div>
            </div>

            {/* Crypto TX Hash — only for the crypto leg */}
            {(() => {
              const cryptoWallet = wallets.find(w => w.id === walletOutId);
              if (!cryptoWallet || cryptoWallet.type !== 'crypto') return null;
              return (
                <div className="mt-4">
                  <TxHashField
                    walletAddress={cryptoWallet.address}
                    walletType={cryptoWallet.type}
                    value={watch('cryptoTxHash') || ''}
                    onChange={(v) => setValue('cryptoTxHash', v)}
                  />
                </div>
              );
            })()}

            {/* Pending Deal extra fields */}
            {isPendingDeal && (
              <div className="mt-4 p-4 rounded-lg border border-ctp-peach/30 bg-ctp-peach/5 flex flex-col gap-4">
                {/* Debt Amounts */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                        {t('cashier.createOperation.debtIn', 'Нам должны')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setValue('debtAmountIn', mode === 'buy' ? clientGivesAmount : clientGetsAmount)}
                        className="text-[10px] bg-ctp-surface-hover hover:bg-ctp-peach/20 text-ctp-peach px-2 py-0.5 rounded border border-ctp-peach/30 transition-colors"
                      >
                        {t('cashier.createOperation.fill', 'Fill')}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full py-2 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-peach"
                        placeholder="0.00"
                        {...register('debtAmountIn')}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground-muted">
                        {mode === 'buy' ? watch('clientGivesCurrency') : getClientGetsCurrency()}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide">
                        {t('cashier.createOperation.debtOut', 'Мы должны')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setValue('debtAmountOut', mode === 'buy' ? clientGetsAmount : clientGivesAmount)}
                        className="text-[10px] bg-ctp-surface-hover hover:bg-ctp-peach/20 text-ctp-peach px-2 py-0.5 rounded border border-ctp-peach/30 transition-colors"
                      >
                        {t('cashier.createOperation.fill', 'Fill')}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full py-2 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none focus:border-ctp-peach"
                        placeholder="0.00"
                        {...register('debtAmountOut')}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground-muted">
                        {mode === 'buy' ? getClientGetsCurrency() : watch('clientGivesCurrency')}
                      </div>
                    </div>
                  </div>
                </div>
                
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Client Information */}
        <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden flex flex-col">
          {/* Panel Header */}
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground m-0">
              {t('cashier.createOperation.clientInformation', 'Client Information')}
            </h2>
          </div>

          {/* Client Content */}
          <div className="p-6 flex flex-col gap-4 flex-1">
            {/* Client Search */}
            <ClientSearchAutocomplete
              value={watch('clientId')}
              onChange={(clientId) => setValue('clientId', clientId)}
              onClientSelect={setSelectedClient}
              onClearError={() => clearErrors('clientId')}
              error={getError('clientId')}
              placeholder={t('cashier.createOperation.searchPlaceholder', 'Search...')}
            />

            {/* Selected Client Card */}
            {selectedClient && (
              <div className="border border-border rounded-lg p-4 bg-surface-hover/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-ctp-overlay0/30 flex items-center justify-center">
                      <User size={20} className="text-foreground-muted" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{t('cashier.createOperation.client', 'Client')}</div>
                      <div className="text-xs text-foreground-muted">{selectedClient.uniqueId || '-'}</div>
                    </div>
                  </div>
                  <div className="relative" ref={clientMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowClientMenu(!showClientMenu)}
                      className="text-foreground-muted hover:text-foreground p-1 -m-1 rounded hover:bg-surface-hover transition-colors"
                    >
                      <MoreVertical size={18} />
                    </button>

                    {/* Dropdown Menu */}
                    {showClientMenu && (
                      <div className="absolute right-0 top-full mt-1 w-44 bg-surface border border-border rounded-lg shadow-lg z-50 py-1 overflow-hidden">
                        <button
                          type="button"
                          onClick={handleClearClient}
                          className="w-full px-3 py-2 text-sm text-left text-ctp-red hover:bg-surface-hover flex items-center gap-2 transition-colors"
                        >
                          <X size={14} />
                          {t('cashier.createOperation.clearClient', 'Clear Selection')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Details */}
                <div className="space-y-3 text-sm">
                  {selectedClient.name && (
                    <div>
                      <div className="text-xs text-foreground-muted">{t('cashier.createOperation.name', 'Name')}</div>
                      <div className="text-foreground">{selectedClient.name}</div>
                    </div>
                  )}
                  {selectedClient.description && (
                    <div>
                      <div className="text-xs text-foreground-muted">{t('cashier.createOperation.description', 'Description')}</div>
                      <div className="text-foreground">{selectedClient.description}</div>
                    </div>
                  )}
                  {selectedClient.transactionCount !== undefined && (
                    <div>
                      <div className="text-xs text-foreground-muted">{t('cashier.createOperation.transactions', 'Transactions')}</div>
                      <div className="text-foreground">{selectedClient.transactionCount}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button - pushed to bottom */}
            <button
              type="submit"
              className={`
                flex items-center justify-center gap-2.5 w-full py-4 px-6
                text-white border-none rounded-xl text-[15px] font-bold
                cursor-pointer transition-all active:scale-95 mt-auto
                shadow-lg
                disabled:opacity-60 disabled:cursor-not-allowed
                ${mode === 'buy'
                  ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600'
                  : 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600'
                }
              `}
              disabled={isSubmitting || walletsLoading}
            >
              {mode === 'buy' ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
              {isSubmitting
                ? t('cashier.createOperation.processing', 'Processing...')
                : mode === 'buy'
                  ? t('cashier.createOperation.buy', 'BUY')
                  : t('cashier.createOperation.sell', 'SELL')
              }
            </button>
          </div>
        </div>
      </form>

      {/* Success Modal */}
      <OperationSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        data={successData}
      />
    </>
  );
}
